import React, { Component } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import Select from 'components/controls/select';
import {
  validateSessionObjectAlias,
  SESSION_OBJECT_ALIAS_HTML5_PATTERN,
  SESSION_OBJECT_ALIAS_MAX_LENGTH,
} from 'context/sessionObjectAlias';

export default class CreateSessionObjectModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      alias: "",
      objectType: "message",
      validated: false
    };
    this.formRef = React.createRef();
  }

  handleCreate(event) {
    event.preventDefault();
    event.stopPropagation();

    const form = this.formRef.current;
    const { alias, objectType } = this.state;
    const { existingAliases } = this.props;

    // Check HTML5 validation first
    if (!form.checkValidity()) {
      this.setState({ validated: true });
      return;
    }

    // Grammar validation (covers the "."/".." segment rule the HTML5 pattern can't express).
    const aliasError = validateSessionObjectAlias(alias);
    if (aliasError) {
      form.elements.alias.setCustomValidity(aliasError);
      this.setState({ validated: true });
      return;
    }

    // Custom validation for uniqueness
    if (existingAliases && existingAliases.includes(alias)) {
      form.elements.alias.setCustomValidity("Alias must be unique within the scope of the session.");
      this.setState({ validated: true });
      return;
    }

    this.props.onSave(alias, objectType);
    this.props.onHide();
    this.resetState();
  }

  resetState() {
    this.setState({
      alias: "",
      objectType: "message",
      validated: false
    });
  }

  handleClose() {
    this.props.onHide();
    this.resetState();
  }

  handleAliasChange(value) {
    // Clear custom validity when user types
    if (this.formRef.current) {
      this.formRef.current.elements.alias.setCustomValidity("");
    }
    this.setState({ alias: value });
  }

  render() {
    return (
      <Modal
        show={this.props.show}
        onHide={() => this.handleClose()}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Create Session Object</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ marginBottom: "20px" }}>
            Session Object is a key-value pair stored within the session context. 
            It can be used to store configuration, specialized data, or shared state accessible by workflow steps.
          </div>
          
          <hr />

          <Form 
            noValidate 
            validated={this.state.validated} 
            onSubmit={(e) => this.handleCreate(e)}
            ref={this.formRef}
          >
            <Form.Group>
              <Form.Label><strong>Alias</strong></Form.Label>
              <Form.Control
                type="text"
                name="alias"
                placeholder="Enter object alias"
                value={this.state.alias}
                onChange={(e) => this.handleAliasChange(e.target.value)}
                required
                minLength={1}
                maxLength={SESSION_OBJECT_ALIAS_MAX_LENGTH}
                pattern={SESSION_OBJECT_ALIAS_HTML5_PATTERN}
              />
              <Form.Control.Feedback type="invalid">
                Enter a valid alias: 1&ndash;256 chars; path segments of A&ndash;Z a&ndash;z 0&ndash;9 . _ @ -
                separated by "/"; no "." or ".." segment.
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                Session object alias must conform to the following requirements:
                <ul>
                  <li>1 to 256 characters;</li>
                  <li>each "/"-separated segment uses letters, digits, and <strong>. _ @ -</strong>;</li>
                  <li>
                    no <strong>.</strong> or <strong>..</strong> segment
                    (e.g. <code>missions/a1b2c3/graph</code>);
                  </li>
                  <li>must be unique within the scope of the session.</li>
                </ul>
              </Form.Text>
            </Form.Group>
            <Form.Group style={{ marginTop: "15px" }}>
              <Form.Label><strong>Object Type</strong></Form.Label>
              <div style={{ maxWidth: "200px" }}>
                <Select
                  options={[
                    { value: "message", label: "message" },
                    { value: "thread", label: "thread" },
                    { value: "graph", label: "graph" }
                  ]}
                  value={{ value: this.state.objectType, label: this.state.objectType }}
                  onChange={(option) => this.setState({ objectType: option.value })}
                />
              </div>
              <Form.Text className="text-muted">
                Select the type of object to create.
              </Form.Text>
            </Form.Group>
            <button type="submit" style={{ display: "none" }} />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => this.handleClose()}>
            Cancel
          </Button>
          <Button variant="success" onClick={(e) => this.handleCreate(e)}>
            Create & Edit
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}
